import { useState, useEffect, useCallback } from 'react'
import { AlertTriangle, X, MapPin } from 'lucide-react'

interface CrisisModeProps {
  compositeScore: number
  level: string
  topCountry?: { code: string; name: string; score: number } | null
  onAutoZoom?: (lat: number, lng: number) => void
  onFilterHighSev?: () => void
}

const ACTIVATE_THRESHOLD = 80
const REARM_THRESHOLD = 60

const COUNTRY_COORDS: Record<string, [number, number]> = {
  AF: [34.5, 69.2], AL: [41.3, 19.8], DZ: [36.8, 3.1], AR: [-34.6, -58.4],
  AU: [-33.9, 151.2], BR: [-15.8, -47.9], CA: [45.4, -75.7], CN: [39.9, 116.4],
  EG: [30.0, 31.2], FR: [48.9, 2.3], DE: [52.5, 13.4], IN: [28.6, 77.2],
  ID: [-6.2, 106.8], IR: [35.7, 51.4], IQ: [33.3, 44.4], IL: [31.8, 35.2],
  IT: [41.9, 12.5], JP: [35.7, 139.7], KP: [39.0, 125.8], KR: [37.6, 127.0],
  LB: [33.9, 35.5], LY: [32.9, 13.2], MX: [19.4, -99.1], NG: [9.1, 7.5],
  PK: [33.7, 73.0], PH: [14.6, 121.0], PL: [52.2, 21.0], RU: [55.8, 37.6],
  SA: [24.7, 46.7], SY: [33.5, 36.3], TW: [25.0, 121.5], TR: [39.9, 32.9],
  UA: [50.4, 30.5], AE: [24.5, 54.7], GB: [51.5, -0.1], US: [38.9, -77.0],
  VE: [10.5, -66.9], YE: [15.4, 44.2], ZA: [-25.7, 28.2], MM: [19.76, 96.1],
  ET: [9.0, 38.7], SD: [15.6, 32.5], SO: [2.0, 45.3], CD: [-4.3, 15.3],
}

export default function CrisisMode({
  compositeScore,
  level,
  topCountry,
  onAutoZoom,
  onFilterHighSev,
}: CrisisModeProps) {
  const [isActive, setIsActive] = useState(false)
  const [wasDismissed, setWasDismissed] = useState(false)
  const [droppedBelowRearm, setDroppedBelowRearm] = useState(false)

  const activate = useCallback(() => {
    setIsActive(true)
    setWasDismissed(false)
    setDroppedBelowRearm(false)

    if (topCountry && onAutoZoom) {
      const coords = COUNTRY_COORDS[topCountry.code]
      if (coords) onAutoZoom(coords[0], coords[1])
    }

    onFilterHighSev?.()
  }, [compositeScore, topCountry, onAutoZoom, onFilterHighSev])

  useEffect(() => {
    if (compositeScore < REARM_THRESHOLD) {
      setDroppedBelowRearm(true)
      if (isActive) setIsActive(false)
    }
  }, [compositeScore, isActive])

  useEffect(() => {
    const shouldActivate = compositeScore >= ACTIVATE_THRESHOLD && level === 'critical'

    if (!shouldActivate) return

    if (!isActive && !wasDismissed) {
      activate()
    } else if (!isActive && wasDismissed && droppedBelowRearm) {
      activate()
    }
  }, [compositeScore, level, isActive, wasDismissed, droppedBelowRearm, activate])

  const handleDismiss = useCallback(() => {
    setIsActive(false)
    setWasDismissed(true)
    setDroppedBelowRearm(false)
  }, [])

  if (!isActive) return null

  return (
    <>
      <style>{`
        @keyframes crisis-border-pulse {
          0%, 100% { box-shadow: inset 0 0 30px rgba(239,68,68,0.15), 0 0 60px rgba(239,68,68,0.1); border-color: rgba(239,68,68,0.5); }
          50% { box-shadow: inset 0 0 60px rgba(239,68,68,0.3), 0 0 100px rgba(239,68,68,0.2); border-color: rgba(239,68,68,0.8); }
        }
        @keyframes crisis-text-flash {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes crisis-banner-bg {
          0%, 100% { background-color: rgba(69,10,10,0.92); }
          50% { background-color: rgba(127,29,29,0.95); }
        }
        @keyframes crisis-scan {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100vh); }
        }
      `}</style>

      {/* Viewport border pulse */}
      <div
        className="fixed inset-0 z-[90] pointer-events-none border-2 border-red-500/50"
        style={{ animation: 'crisis-border-pulse 2s ease-in-out infinite' }}
      />

      {/* Scan line overlay */}
      <div className="fixed inset-0 z-[91] pointer-events-none overflow-hidden">
        <div
          className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-red-500/30 to-transparent"
          style={{ animation: 'crisis-scan 1.5s linear infinite' }}
        />
      </div>

      {/* Corner flare markers */}
      <div className="fixed top-0 left-0 z-[92] pointer-events-none">
        <div className="w-8 h-8 border-t-2 border-l-2 border-red-500/70" />
      </div>
      <div className="fixed top-0 right-0 z-[92] pointer-events-none">
        <div className="w-8 h-8 border-t-2 border-r-2 border-red-500/70 ml-auto" />
      </div>
      <div className="fixed bottom-0 left-0 z-[92] pointer-events-none">
        <div className="w-8 h-8 border-b-2 border-l-2 border-red-500/70" />
      </div>
      <div className="fixed bottom-0 right-0 z-[92] pointer-events-none">
        <div className="w-8 h-8 border-b-2 border-r-2 border-red-500/70 ml-auto" />
      </div>

      {/* Crisis banner */}
      <div className="fixed top-10 left-1/2 -translate-x-1/2 z-[95]" style={{ pointerEvents: 'auto' }}>
        <div
          className="border border-red-500/60 rounded px-5 py-2.5 font-mono shadow-[0_0_20px_rgba(239,68,68,0.4)]"
          style={{ animation: 'crisis-banner-bg 1.5s ease-in-out infinite' }}
        >
          <div className="flex items-center gap-4">
            <AlertTriangle
              className="w-5 h-5 text-red-400 shrink-0"
              style={{ animation: 'crisis-text-flash 0.8s ease-in-out infinite' }}
            />

            <div className="flex items-center gap-5">
              <div className="flex flex-col items-center">
                <span
                  className="text-[10px] text-red-300/70 uppercase tracking-[0.2em]"
                >
                  Crisis Mode
                </span>
                <span
                  className="text-sm font-bold text-red-400 tracking-wider"
                  style={{ animation: 'crisis-text-flash 1s ease-in-out infinite' }}
                >
                  ACTIVE
                </span>
              </div>

              <div className="w-px h-8 bg-red-500/30" />

              <div className="flex flex-col">
                <span className="text-[9px] text-red-300/50 uppercase tracking-[0.15em]">Escalation</span>
                <span className="text-lg font-bold text-red-300 tabular-nums">
                  {Math.round(compositeScore)}
                </span>
              </div>

              <div className="w-px h-8 bg-red-500/30" />

              <div className="flex flex-col">
                <span className="text-[9px] text-red-300/50 uppercase tracking-[0.15em]">Level</span>
                <span className="text-xs font-bold text-red-400 uppercase">{level}</span>
              </div>

              {topCountry && (
                <>
                  <div className="w-px h-8 bg-red-500/30" />

                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-3 h-3 text-red-400/70" />
                    <div className="flex flex-col">
                      <span className="text-[9px] text-red-300/50 uppercase tracking-[0.15em]">Hotspot</span>
                      <span className="text-xs font-bold text-red-300">
                        {topCountry.name}
                        <span className="text-red-500/60 ml-1 text-[10px]">
                          {topCountry.score.toFixed(0)}
                        </span>
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="w-px h-8 bg-red-500/30" />

            <button
              onClick={handleDismiss}
              className="flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] text-red-300/80 border border-red-500/40 rounded hover:bg-red-500/20 hover:text-red-200 transition-colors"
            >
              <X className="w-3 h-3" />
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
